import React, {
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
} from 'react';
import { formatPct } from '../dummyData';
import { SubscriptionSummary } from '../../../../types/subscription';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../store/slices/subscription';
import { selectChartData } from '../../../../store/slices/chartData';
import { selectUserId } from '../../../../store/slices/user';
import { selectIsDemo } from '../../../../store/slices/demo';
import { requestSubscriptionDetails } from '../../detailsLoader';
import { useNavigate } from 'react-router-dom';
import AssetIcon from '../../../../components/AssetIcon';
import { useFilter } from '../FilterContext';
import { RootState } from '../../../../store/store';
import './index.css';

// ── Sparkline ────────────────────────────────────────────────────────────────

const Spark: React.FC<{
  dataPoints?: Array<{ value: number; timestamp: number }>;
  profit: boolean;
}> = ({ dataPoints, profit }) => {
  const stroke = profit ? '#00B061' : '#F0162F';
  const fill = profit ? 'rgba(0,176,97,0.16)' : 'rgba(240,22,47,0.16)';

  const { line, area } = useMemo(() => {
    if (!dataPoints?.length || dataPoints.length < 2) {
      return { line: '', area: '' };
    }
    const values = dataPoints.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const w = 100;
    const h = 30;
    const pad = 2;

    const coords = dataPoints.map((d, i) => {
      const x = (i / (dataPoints.length - 1)) * w;
      const y = h - pad - ((d.value - min) / range) * (h - pad * 2);
      return `${x.toFixed(1)} ${y.toFixed(1)}`;
    });

    const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c}`).join(' ');
    const area = `${line} L${w} ${h} L0 ${h} Z`;
    return { line, area };
  }, [dataPoints]);

  if (!line) {
    return <div className="bot-ticker-spark-empty" />;
  }

  return (
    <svg
      viewBox="0 0 100 30"
      width="100%"
      height="100%"
      preserveAspectRatio="none"
    >
      <path d={area} fill={fill} stroke="none" />
      <path d={line} stroke={stroke} strokeWidth="1.4" fill="none" />
    </svg>
  );
};

// ── Ticker Item (Design C minimalistic) ──────────────────────────────────────

interface TickerItemProps {
  sub: SubscriptionSummary;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent, subId: number) => void;
}

const TickerItem: React.FC<TickerItemProps> = ({
  sub,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}) => {
  // Read the shared chart cache so a chart fetched here OR by the grid shows up
  // in both places (it previously read sub.chartData, which is never populated).
  const chartData = useSelector(selectChartData(sub.id));
  const currentProfit = sub.currentProfit ?? 0;
  const currentPositive = currentProfit >= 0;
  const latestOrderTs = sub.latestTransaction?.transactionTimestamp;

  const sparkPoints = useMemo<
    Array<{ value: number; timestamp: number }>
  >(() => {
    // Show the total-VALUE series (same as the grid's mini chart) — it moves
    // with price and is non-flat. The balance series (stable.all / asset.all)
    // are depleted to ~0 right after a trade, so they render as flat lines.
    const series = chartData?.dataPoints?.total?.stable ?? [];
    if (series.length < 2) return [];

    // Prefer the window since the last order (recent performance); fall back to
    // the last 40 points so the sparkline always has shape.
    if (latestOrderTs) {
      const sinceOrder = series.filter(
        (d: any) => d.timestamp >= latestOrderTs,
      );
      if (sinceOrder.length >= 8) return sinceOrder;
    }
    return series.slice(-40);
  }, [chartData, latestOrderTs]);

  return (
    <div
      className="bot-ticker-item"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => onPointerUp(e, sub.id)}
    >
      <div className="bot-ticker-row">
        <AssetIcon
          baseAsset={sub.baseAsset}
          width={20}
          height={20}
          style={{ marginBottom: 0 }}
        />
        <span className="bot-ticker-pair">{sub.baseAsset}</span>
        <span
          className={`bot-ticker-profit ${
            currentPositive
              ? 'bot-ticker-profit--pos'
              : 'bot-ticker-profit--neg'
          }`}
        >
          {formatPct(currentProfit)}
        </span>
      </div>
      <div className="bot-ticker-spark">
        <Spark dataPoints={sparkPoints} profit={currentPositive} />
      </div>
    </div>
  );
};

// ── BotTicker Container ──────────────────────────────────────────────────────

interface BotTickerProps {
  subscriptions: SubscriptionSummary[];
}

const DRAG_THRESHOLD = 6;
const FIRST_BATCH = 15; // items fetched up front (visible + buffer)
const PREFETCH_CHUNK = 5; // items fetched each time the scroll nears the edge

const BotTicker: React.FC<BotTickerProps> = ({ subscriptions }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(selectUserId);
  const isDemo = useSelector(selectIsDemo);
  // detailsLoader picks the right endpoint per session (real vs demo) using
  // its own store read; userId is only consulted in the real path, so demo
  // mode can pass 0 here without consequences.
  const fetchUserId = userId ?? 0;
  const { sortedSubs } = useFilter();
  // Use the sorted/filtered subs from the global FilterContext so the ticker
  // mirrors the leaderboard / subscription list ordering. Fall back to the
  // raw prop when the filter hasn't produced any (initial render edge case).
  const orderedSubs = sortedSubs.length ? sortedSubs : subscriptions;
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const isHovered = useRef(false);
  const isDragging = useRef(false);
  const pointerId = useRef<number | null>(null);
  const dragStartX = useRef(0);
  const dragStartOffset = useRef(0);
  const didDrag = useRef(false);
  const [, forceMount] = useState(0);

  // Scroll-driven prefetch state (refs so the rAF loop reads them live).
  const reduxStore = useStore();
  const canScrollRef = useRef(false); // gate: scroll only after first batch loads
  const fetchedCountRef = useRef(0); // how many items (in order) we've requested
  const gateStartRef = useRef(0);

  // Stable key for the sub-id set so the effects don't restart on every render
  // (orderedSubs is a fresh array reference each render).
  const idsKey = useMemo(
    () => orderedSubs.map((sub) => sub.id).join(','),
    [orderedSubs],
  );

  // Fetch the first batch up front. The rest are fetched scroll-driven (below):
  // the auto-scroll only starts once this batch is ready, then each further
  // chunk is requested as the next not-yet-fetched item approaches view.
  useEffect(() => {
    if ((!userId && !isDemo) || !idsKey) {
      return;
    }

    const ids = idsKey.split(',').map(Number);
    canScrollRef.current = false;
    fetchedCountRef.current = 0;
    gateStartRef.current = performance.now();

    const first = Math.min(FIRST_BATCH, ids.length);
    ids
      .slice(0, first)
      .forEach((id) => requestSubscriptionDetails(fetchUserId, id));
    fetchedCountRef.current = first;
  }, [userId, isDemo, fetchUserId, idsKey]);

  // Re-measure half-width on layout/resize
  useLayoutEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (!track) return;
      halfWidthRef.current = track.scrollWidth / 2;
    };
    measure();
    forceMount((n) => n + 1);
    const obs = new ResizeObserver(measure);
    if (trackRef.current) obs.observe(trackRef.current);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [orderedSubs]);

  // Smooth transform-based animation + scroll-driven prefetch.
  useEffect(() => {
    if (!idsKey) return;

    const ids = idsKey.split(',').map(Number);
    const speed = 22; // px/sec
    const PREFETCH_LEAD_PX = 540; // ~3 items of lead before an item enters view
    const GATE_TIMEOUT_MS = 8000; // don't freeze forever if the batch fails
    let last = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const track = trackRef.current;

      // Gate: hold the scroll until the first batch's charts are in the store
      // (or a timeout), so scrolling only begins once the visible items render.
      if (!canScrollRef.current) {
        const firstCount = fetchedCountRef.current;
        const data = (reduxStore.getState() as RootState).chartData.data;
        const ready =
          firstCount > 0 && ids.slice(0, firstCount).every((id) => !!data[id]);
        if (ready || now - gateStartRef.current > GATE_TIMEOUT_MS) {
          canScrollRef.current = true;
        }
      }

      if (track) {
        if (canScrollRef.current && !isHovered.current && !isDragging.current) {
          offsetRef.current += speed * dt;
        }
        const half = halfWidthRef.current;
        if (half > 0) {
          if (offsetRef.current >= half) offsetRef.current -= half;
          if (offsetRef.current < 0) offsetRef.current += half;
        }
        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;

        // Prefetch the next chunk when the first not-yet-fetched item is about
        // to scroll into view — paces fetching with the actual scroll.
        if ((userId || isDemo) && fetchedCountRef.current < ids.length) {
          const itemWidth =
            halfWidthRef.current > 0 ? halfWidthRef.current / ids.length : 180;
          const containerWidth = containerRef.current?.clientWidth ?? 0;
          const firstUnfetchedX = fetchedCountRef.current * itemWidth;

          if (
            offsetRef.current + containerWidth + PREFETCH_LEAD_PX >=
            firstUnfetchedX
          ) {
            ids
              .slice(
                fetchedCountRef.current,
                fetchedCountRef.current + PREFETCH_CHUNK,
              )
              .forEach((id) => requestSubscriptionDetails(fetchUserId, id));
            fetchedCountRef.current += PREFETCH_CHUNK;
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [idsKey, userId, isDemo, fetchUserId, reduxStore]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button !== undefined && e.button !== 0) return;
    isDragging.current = true;
    didDrag.current = false;
    pointerId.current = e.pointerId;
    dragStartX.current = e.clientX;
    dragStartOffset.current = offsetRef.current;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current || pointerId.current !== e.pointerId) return;
    const delta = e.clientX - dragStartX.current;
    if (Math.abs(delta) > DRAG_THRESHOLD) {
      didDrag.current = true;
      // capture once we've crossed the threshold so subsequent moves outside
      // the original element still feed back to us
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {}
    }
    offsetRef.current = dragStartOffset.current - delta;
  };

  const handlePointerUp = (e: React.PointerEvent, subId: number) => {
    const wasDragging = isDragging.current;
    isDragging.current = false;
    if (pointerId.current === e.pointerId) pointerId.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
    if (!wasDragging || didDrag.current) return;
    // Click — navigate
    dispatch(setActiveSubscriptionId(subId));
    navigate('/sub');
  };

  const handleContainerPointerLeave = () => {
    isHovered.current = false;
  };

  // Duplicate for seamless wrap
  const items = [...orderedSubs, ...orderedSubs];

  return (
    <div
      className="bot-ticker-container"
      ref={containerRef}
      onMouseEnter={() => {
        isHovered.current = true;
      }}
      onMouseLeave={handleContainerPointerLeave}
    >
      <div className="bot-ticker-track" ref={trackRef}>
        {items.map((sub, i) => (
          <TickerItem
            key={`${sub.id}_${i}`}
            sub={sub}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        ))}
      </div>
    </div>
  );
};

export default BotTicker;
